// src/services/subjectService.tsx
import apiClient from "../api/apiClient";

const TENANT_ID = process.env.REACT_APP_TENANT_ID ?? "1";

// --- Types ---
export interface Subject {
    id: number;
    name: string;
    classId?: number;
    mediumId?: number;
    schoolClass?: { id: number; name: string }; // Handle potential nested objects
    medium?: { id: number; name: string };
    monthlyPrice?: number;
    quarterlyPrice?: number;
    halfYearlyPrice?: number;
    yearlyPrice?: number;
    purchasable: boolean;
}

export interface SubjectUpsertDto {
    classId: number;
    mediumId: number;
    name: string;
    monthlyPrice: number;
    quarterlyPrice: number;
    halfYearlyPrice: number;
    yearlyPrice: number;
    purchasable: boolean;
}

// --- Existing Helpers ---

async function tryGetOrPost(relPath: string, query: Record<string, any> = {}, postBody?: any) {
    const qs = new URLSearchParams({ ...query, tenantId: TENANT_ID });
    const url = `${relPath}${qs.toString() ? `?${qs.toString()}` : ""}`;
    try {
        const resp = await apiClient.get(url);
        return resp.data;
    } catch (err: any) {
        if (err?.response?.status === 405) {
            const resp = await apiClient.post(relPath, postBody ?? { ...query, tenantId: TENANT_ID });
            return resp.data;
        }
        throw err;
    }
}

export async function getClasses(page = 0, size = 20) {
    const resp = await apiClient.get(`/classes`, {
        params: { page, size }
    });
    const data = resp.data;
    if (Array.isArray(data)) return data;
    return data?.content ?? [];
}

export async function getMediums() {
    const resp = await apiClient.get("/mediums");
    const data = resp.data;
    if (Array.isArray(data)) return data;
    return data?.content ?? [];
}

/**
 * Public/Student view of subjects (filtered by Category).
 */
export async function getSubjects(params?: { classId?: number; mediumId?: number }) {
    const resp = await apiClient.get("/subjects", {
        params: {
            classId: params?.classId,
            mediumId: params?.mediumId
        }
    });
    const data = resp.data;
    if (Array.isArray(data)) return data;
    return data?.content ?? [];
}

// --- NEW: Admin Subject Management ---

/**
 * Get ALL subjects (Admin View).
 * GET /api/admin/subjects/all
 */
export async function getAllSubjectsAdmin(page = 0, size = 50) {
    const resp = await apiClient.get("/admin/subjects/all", {
        params: { page, size, tenantId: TENANT_ID }
    });
    return resp.data;
}

/**
 * Create a new subject.
 * POST /api/admin/subjects
 */
export async function createSubject(payload: SubjectUpsertDto) {
    const resp = await apiClient.post("/admin/subjects", payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

/**
 * Update an existing subject.
 * PUT /api/admin/subjects/{id}
 */
export async function updateSubject(id: number, payload: SubjectUpsertDto) {
    const resp = await apiClient.put(`/admin/subjects/${id}`, payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

/**
 * Delete a subject.
 * DELETE /api/admin/subjects/{id}
 */
export async function deleteSubject(id: number) {
    const resp = await apiClient.delete(`/admin/subjects/${id}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

// --- Class Management ---

export interface ClassUpsertDto {
    name: string;
}

export async function createClass(payload: ClassUpsertDto) {
    const resp = await apiClient.post('/admin/classes', payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function updateClass(id: number, payload: ClassUpsertDto) {
    const resp = await apiClient.put(`/admin/classes/${id}`, payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function deleteClass(id: number) {
    const resp = await apiClient.delete(`/admin/classes/${id}`, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}